import { shiftIntervalLeft, shiftIntervalRight } from "@/common/intervals";
import { getName } from "@/common/names";
import { HGInterval, HGIntervalType } from "@/common/types";
import {
  GetParentInterval,
  IntervalsRange,
  MapInterval,
  MapIntervals,
} from "@/engine/engine";
import { useEffect, useRef, useState, useCallback, memo } from "react";
import { GroupedVirtuoso, GroupedVirtuosoHandle } from "react-virtuoso";
import { SidebarMenuItem } from "../sidebar";
import { Button } from "../button";
import { HGFocus, HGStacks } from "@/stack/stacks";
import { getChildType, getMaxN } from "@/common/intervalTypes";
import { getNow } from "@/main";
import { useNowTick } from "@/common/hooks";
import { safeShortName } from "@/common/recordTree";
import StacksHeader, { StacksQualifiers } from "./StacksHeader";

type GroupInfo = {
  interval: HGInterval;
  name: string;
  count: number;
  first: bigint;
};

/**
 * How a row relates to the selected range. The two endpoints may sit at
 * different levels, so a row at the rendered level can be covered only in
 * part — "2024" is a boundary row for a range that ends on a minute in it.
 */
type RowKind =
  | "outside"
  | "covered"
  /** The range starts partway inside this row. */
  | "partialStart"
  /** The range stops partway inside this row. */
  | "partialEnd"
  /** Exactly the focused endpoint. */
  | "focused"
  /** Exactly the other endpoint (only when it shares the rendered level). */
  | "other";

// The top-level type ("byr") has no parent to group by, and unlike every other
// level it is finite, so its whole range is listed at once: one headerless
// group per era, BC (-maxN..-1) then AD (1..maxN). n has no zero, hence the
// split — an item's n is its group's first n plus its index in the group.
const TOP_LEVEL_FALLBACK_MAX_N = 1000n;

// Marks the interval holding the present moment. It sits outside the flow so a
// row's name stays centered whether or not it is "now", and inherits the row's
// colour so it reads the same on selected and unselected rows.
const NowDot = () => (
  <span
    aria-hidden
    className="absolute left-2 top-1/2 size-[5px] -translate-y-1/2 rounded-full bg-current opacity-70"
  />
);

const generateTopLevelGroups = (interval: HGInterval) => {
  const maxN = getMaxN(interval.it) ?? TOP_LEVEL_FALLBACK_MAX_N;
  const count = Number(maxN);
  const groups: GroupInfo[] = [
    { interval: { n: -maxN, it: interval.it }, name: "", count, first: -maxN },
    { interval: { n: 1n, it: interval.it }, name: "", count, first: 1n },
  ];

  return {
    groups,
    initialTopMostItemIndexValue: Number(
      interval.n < 0n ? interval.n + maxN : maxN + interval.n - 1n,
    ),
  };
};

const generateGroups = (interval: HGInterval, parentIt?: HGIntervalType) => {
  if (!parentIt) return generateTopLevelGroups(interval);

  const groups: GroupInfo[] = [];

  const parentInterval = GetParentInterval(interval, parentIt);

  // center group - for incoming interval
  const span = MapInterval(parentInterval, interval.it);
  let count = Number(span.end.n - span.begin.n + 1n);
  const group: GroupInfo = {
    interval: parentInterval,
    name: getName(parentInterval),
    count,
    first: span.begin.n,
  };
  groups.push(group);

  let initialTopMostItemIndexValue = Number(interval.n - span.begin.n);

  // generate groups around center group
  let i = 1;
  while (count < 1001) {
    const leftParentInterval = shiftIntervalLeft(parentInterval, i);
    const rightParentInterval = shiftIntervalRight(parentInterval, i);
    const leftSpan = MapInterval(leftParentInterval, interval.it);
    const rightSpan = MapInterval(rightParentInterval, interval.it);
    const leftCount = Number(leftSpan.end.n - leftSpan.begin.n + 1n);
    initialTopMostItemIndexValue += leftCount;
    const rightCount = Number(rightSpan.end.n - rightSpan.begin.n + 1n);
    groups.unshift({
      interval: leftParentInterval,
      name: getName(leftParentInterval),
      count: leftCount,
      first: leftSpan.begin.n,
    });
    groups.push({
      interval: rightParentInterval,
      name: getName(rightParentInterval),
      count: rightCount,
      first: rightSpan.begin.n,
    });
    count += leftCount + rightCount;
    i = i + 1;
  }

  return {
    groups: groups,
    initialTopMostItemIndexValue: initialTopMostItemIndexValue,
  };
};

// 0-based item index within `groups`, excluding group headers and NOT offset
// by firstItemIndex — that is the coordinate space both scrollToIndex and
// initialTopMostItemIndex use (see indexInGroup, which subtracts
// firstItemIndex for the opposite conversion). Groups are contiguous ranges
// of n, so a linear scan suffices; null when n isn't in any loaded group.
const localIndexOf = (groups: GroupInfo[], n: bigint): number | null => {
  let offset = 0;
  for (const g of groups) {
    if (n >= g.first && n < g.first + BigInt(g.count)) {
      return offset + Number(n - g.first);
    }
    offset += g.count;
  }
  return null;
};

// Maps a row's relation to the range onto shadcn button variants plus corner
// surgery, so a run of rows fuses into one vertical pill. Boundary rows — where
// the range starts or stops partway through — carry a left accent bar and a
// caption naming the precise endpoint, since the row itself cannot show it.
const ROW_STYLES: Record<
  RowKind,
  { variant: "ghost" | "default" | "secondary"; className: string }
> = {
  focused: { variant: "default", className: "" },
  other: { variant: "default", className: "" },
  covered: { variant: "secondary", className: "" },
  partialStart: { variant: "secondary", className: "" },
  partialEnd: { variant: "secondary", className: "" },
  outside: { variant: "ghost", className: "opacity-70" },
};

// Marks the half of a boundary row that the range actually covers: a range
// starting partway through this interval covers its bottom half, one ending
// partway through covers its top half.
const BoundaryBar = ({ edge }: { edge: "start" | "end" }) => (
  <span
    aria-hidden
    className={
      "absolute left-0 h-1/2 w-[3px] rounded-full bg-primary " +
      (edge === "start" ? "top-1/2" : "bottom-1/2")
    }
  />
);

type StackItemProps = {
  it: string;
  nStr: string;
  kind: RowKind;
  /** Rounding: whether this row opens / closes the highlighted run. */
  runStart: boolean;
  runEnd: boolean;
  isFocusedEndpoint: boolean;
  isNow: boolean;
  /** The precise endpoint inside a boundary row, e.g. "15 Mar 2025 14:30". */
  caption: string | null;
  onClick: (interval: HGInterval, shiftKey: boolean) => void;
};

const StackItem = memo(
  ({
    it,
    nStr,
    kind,
    runStart,
    runEnd,
    isFocusedEndpoint,
    isNow,
    caption,
    onClick,
  }: StackItemProps) => {
    const itemInterval: HGInterval = { it, n: BigInt(nStr) };
    const style = ROW_STYLES[kind];

    let classNames = style.className;
    if (kind !== "outside") {
      if (runStart && runEnd) {
        // A single-row run keeps its natural pill shape.
      } else if (runStart) {
        classNames += " rounded-b-none";
      } else if (runEnd) {
        classNames += " rounded-t-none";
      } else {
        classNames += " rounded-none";
      }
    }
    if (isFocusedEndpoint) {
      classNames += " ring-2 ring-offset-1 ring-primary";
    }
    if (style.variant !== "default") {
      classNames += isNow ? " text-accent-foreground" : " text-ink2";
    }

    return (
      <Button
        // The picker lives inside the record form; without this the row is a
        // submit button and every interval click saves the record.
        type="button"
        className={
          "relative w-full justify-center font-mono text-[13px] h-auto py-1.5 select-none " +
          classNames
        }
        variant={style.variant}
        size="sm"
        title={isNow ? "Now" : undefined}
        onClick={(e) => onClick(itemInterval, e.shiftKey)}
      >
        {isNow && <NowDot />}
        {kind === "partialStart" && <BoundaryBar edge="start" />}
        {kind === "partialEnd" && <BoundaryBar edge="end" />}
        {getName(itemInterval)}
        {caption && (
          <span className="absolute right-2 font-sans text-[10px] opacity-70">
            {caption}
          </span>
        )}
      </Button>
    );
  },
);

export default function StacksWidget({
  stacks,
  singleMode,
  recenterToken,
  qualifiers,
}: {
  stacks: HGStacks;
  singleMode?: boolean;
  /**
   * EDTF qualifiers for the two endpoints, edited from the header. Passed only
   * where the picker edits a record's time; the sidebar leaves it undefined.
   */
  qualifiers?: StacksQualifiers;
  /**
   * Bumped by a caller to force the list to scroll back to the current
   * position even when neither `it` nor `n` changed (e.g. pressing "Now"
   * while already there but scrolled away). Left `undefined` by consumers
   * that don't need this (e.g. the record-form dialog), which keeps them on
   * today's behaviour exactly.
   */
  recenterToken?: number;
}) {
  const virtuosoRef = useRef<GroupedVirtuosoHandle | null>(null);
  const [groups, setGroups] = useState<GroupInfo[]>([]);
  const [firstInterval, setFirstInterval] = useState<HGInterval | null>(null);
  const [lastInterval, setLastInterval] = useState<HGInterval | null>(null);
  const [firstItemIndex, setFirstItemIndex] = useState<number>(100000000);
  const [initIndex, setInitIndex] = useState<number>(0);
  // Bumped whenever the list is regenerated (as opposed to scrolled in place)
  // so the GroupedVirtuoso key changes and its mount-only
  // initialTopMostItemIndex takes effect again.
  const [listGeneration, setListGeneration] = useState(0);
  const lastAppliedRecenterToken = useRef<number | undefined>(recenterToken);
  const lastFocusRef = useRef<HGFocus>(stacks.getFocus());
  // `groups[].interval.it` is the *parent* type (or, at the top level, the
  // leaf type itself) — not reliable for "are these groups for the current
  // leaf it", so track that separately.
  const loadedItRef = useRef<HGIntervalType | null>(null);

  const focusedStack = stacks.getFocusedStack();
  const interval = focusedStack.getInterval();
  const parentIt = focusedStack.getParentIt();
  useNowTick(interval.it);
  const atTopLevel = !parentIt;
  const focus = stacks.getFocus();
  const otherInterval = stacks.getOtherInterval();

  // Range bounds expressed at the rendered level. IntervalsRange resolves each
  // endpoint through its own interval type, so this is the one primitive that
  // makes a year -> minute range describable as a run of rows.
  const startInterval = stacks.getInterval();
  const endInterval = stacks.getEndInterval();
  const [rangeFrom, rangeTo] = IntervalsRange(
    startInterval,
    endInterval,
    interval.it,
  );

  // Whether the first and last rows of that run are covered entirely, decided
  // in seconds. Level ordering cannot answer this: a week is "finer" than a
  // year yet the first week of 2024 begins in December 2023, so it is only
  // partly covered.
  const [rangeBeginSecond, rangeEndSecond] = stacks.getSecondsRange();
  const fromRowFull =
    MapIntervals({ n: rangeFrom, it: interval.it }, null, "s").begin.n >=
    rangeBeginSecond;
  const toRowFull =
    MapIntervals({ n: rangeTo, it: interval.it }, null, "s").end.n <=
    rangeEndSecond;

  const classifyRow = useCallback(
    (itemN: bigint): RowKind => {
      if (itemN === interval.n) return "focused";
      if (
        otherInterval &&
        otherInterval.it === interval.it &&
        itemN === otherInterval.n
      )
        return "other";
      // Defensive: an inverted pair would make every row "outside" rather than
      // paint a nonsensical band.
      if (rangeFrom > rangeTo || itemN < rangeFrom || itemN > rangeTo)
        return "outside";
      if (itemN === rangeFrom && !fromRowFull) return "partialStart";
      if (itemN === rangeTo && !toRowFull) return "partialEnd";
      return "covered";
    },
    [
      interval.n,
      interval.it,
      otherInterval,
      rangeFrom,
      rangeTo,
      fromRowFull,
      toRowFull,
    ],
  );

  const handleItemClick = useCallback(
    (itemInterval: HGInterval, shiftKey: boolean) => {
      // Desktop shortcut: extend the selection into a range, or adjust the
      // endpoint that is *not* focused without switching chips first.
      if (shiftKey && !singleMode) {
        if (stacks.getEndStack()) {
          stacks.setOtherInterval(itemInterval);
          return;
        }
        const start = stacks.getInterval();
        // An end equal to the start collapses straight back to a single
        // interval, so there is nothing to extend.
        if (itemInterval.n === start.n) return;
        // Both are at the rendered level here (there is no range yet, so the
        // focused endpoint is the start), which makes n comparable.
        const before = itemInterval.n < start.n;
        stacks.setIntervals(
          before ? itemInterval : start,
          before ? start : itemInterval,
        );
        // Focus the endpoint the click created, so it can be refined next.
        stacks.setFocus(before ? "start" : "end");
        return;
      }
      if (itemInterval.n === stacks.getFocusedInterval().n) {
        stacks.pushFocused();
        return;
      }
      stacks.setFocusedInterval(itemInterval);
    },
    [stacks, singleMode],
  );

  // Regenerates the loaded groups (an `it` change, or a same-`it` jump whose
  // target isn't in what's already loaded) or, for a same-`it` jump that IS
  // loaded, scrolls to it in place — but only when recenterToken says a jump
  // actually happened, so an ordinary row click (which never bumps the token)
  // never triggers a scroll.
  useEffect(() => {
    const it = interval.it;
    const localIndex =
      loadedItRef.current === it ? localIndexOf(groups, interval.n) : null;

    if (localIndex === null) {
      const { groups: initialGroups, initialTopMostItemIndexValue } =
        generateGroups(interval, parentIt);
      setGroups(initialGroups);
      setInitIndex(initialTopMostItemIndexValue);
      setFirstItemIndex(100000000);
      setFirstInterval(initialGroups[0].interval);
      setLastInterval(initialGroups[initialGroups.length - 1].interval);
      setListGeneration((g) => g + 1);
      loadedItRef.current = it;
      lastAppliedRecenterToken.current = recenterToken;
      lastFocusRef.current = focus;
      return;
    }

    // Switching chips is a jump even when both endpoints share a level and the
    // target row is already loaded — without this the list would sit still and
    // the header would appear to have done nothing.
    const focusChanged = focus !== lastFocusRef.current;
    lastFocusRef.current = focus;

    if (
      focusChanged ||
      (recenterToken !== undefined &&
        recenterToken !== lastAppliedRecenterToken.current)
    ) {
      lastAppliedRecenterToken.current = recenterToken;
      // scrollToIndex, like initialTopMostItemIndex above, takes the local
      // index within `groups` — it is NOT offset by firstItemIndex (that
      // offset only applies to the index itemContent/groupContent receive).
      virtuosoRef.current?.scrollToIndex({
        index: localIndex,
        align: "center",
        behavior: "smooth",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval.it, interval.n, parentIt, recenterToken, focus]);

  const indexInGroup = (index: number, groupIndex: number) => {
    let i = 0;
    let totalBefore = 0;
    while (i < groupIndex) {
      totalBefore += groups[i].count;
      ++i;
    }
    return index - totalBefore - firstItemIndex;
  };

  // "now" is one n per interval type (see calculateNow()), so a row or a group
  // header is "now" when its n matches the one recorded for its own type.
  const now = getNow();
  const nowN = now[interval.it];
  const nowNStr = nowN === undefined ? null : nowN.toString();
  const parentNowN = parentIt ? now[parentIt] : undefined;

  const groupContent = (index: number) => {
    const group = groups[index];
    if (atTopLevel) {
      // No parent interval to jump to — keep the header as an invisible
      // spacer so the list stays a flat run of buttons.
      return <div className="h-px" />;
    }
    const groupIsNow =
      parentNowN !== undefined && parentNowN === group.interval.n;
    return (
      <SidebarMenuItem className="flex justify-center bg-sidebar px-3 pt-2 pb-1 shadow-[0_2px_4px_-2px_rgb(0_0_0_/_0.15)]">
        <Button
          type="button"
          className={
            "relative w-full rounded-[7px] text-[13px] bg-card shadow-none" +
            (groupIsNow
              ? " text-accent-foreground border-accent-foreground/35"
              : "")
          }
          style={{ fontFamily: "var(--font-serif)" }}
          variant="outline"
          size="sm"
          title={groupIsNow ? "Now" : undefined}
          // Moves the focused endpoint up to its parent, like the breadcrumb.
          // It used to replace the whole selection, which would now silently
          // discard the other endpoint of a range.
          onClick={() => stacks.setFocusedInterval(group.interval)}
        >
          {groupIsNow && <NowDot />}
          {group.name}
        </Button>
      </SidebarMenuItem>
    );
  };

  // Says how far into a boundary row the range reaches. The endpoint's own
  // name is either uninformative ("30" for a minute) or far too long for the
  // row ("15 March 2025, 14:30"), so it is expressed at the row's child level
  // instead: "Mar" inside a year row reads as "starts in March". Computed
  // twice per render, not per row.
  const boundaryCaption = (endpoint: HGInterval, edge: "begin" | "end") => {
    const childIt = getChildType(interval.it);
    if (!childIt) return null;
    const span = MapIntervals(endpoint, null, childIt);
    return safeShortName({
      n: edge === "end" ? span.end.n : span.begin.n,
      it: childIt,
    });
  };

  const startCaption = fromRowFull
    ? null
    : boundaryCaption(startInterval, "begin");
  const endCaption =
    !endInterval || toRowFull ? null : boundaryCaption(endInterval, "end");

  const itemContent = useCallback(
    (index: number, groupIndex: number) => {
      const itemN =
        BigInt(indexInGroup(index, groupIndex)) + groups[groupIndex].first;
      const nStr = itemN.toString();
      const kind = classifyRow(itemN);
      return (
        <StackItem
          it={interval.it}
          nStr={nStr}
          kind={kind}
          runStart={itemN === rangeFrom}
          runEnd={itemN === rangeTo}
          isFocusedEndpoint={kind === "focused" && !!endInterval}
          isNow={nStr === nowNStr}
          caption={
            kind === "partialStart"
              ? startCaption
              : kind === "partialEnd"
                ? endCaption
                : null
          }
          onClick={handleItemClick}
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      groups,
      firstItemIndex,
      nowNStr,
      interval.it,
      classifyRow,
      rangeFrom,
      rangeTo,
      startCaption,
      endCaption,
      endInterval,
      handleItemClick,
    ],
  );
  const handleStartReached = () => {
    if (!firstInterval) return;
    // The top level is listed in full, there is nothing to prepend.
    if (atTopLevel) return;
    const newInterval = shiftIntervalLeft(firstInterval, 1);
    setFirstInterval(newInterval);
    const span = MapInterval(newInterval, interval.it);
    const count = Number(span.end.n - span.begin.n + 1n);
    setFirstItemIndex(firstItemIndex - count);
    setGroups([
      {
        interval: newInterval,
        name: getName(newInterval),
        count: Number(span.end.n - span.begin.n + 1n),
        first: span.begin.n,
      },
      ...groups,
    ]);
  };
  const handleEndReached = () => {
    if (!lastInterval) return;
    // The top level is listed in full, there is nothing to append.
    if (atTopLevel) return;
    const newInterval = shiftIntervalRight(lastInterval, 1);
    setLastInterval(newInterval);
    const span = MapInterval(newInterval, interval.it);
    setGroups([
      ...groups,
      {
        interval: newInterval,
        name: getName(newInterval),
        count: Number(span.end.n - span.begin.n + 1n),
        first: span.begin.n,
      },
    ]);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <StacksHeader
        stacks={stacks}
        singleMode={singleMode}
        qualifiers={qualifiers}
      />
      <div className="min-h-0 grow">
        {groups.length > 0 && (
          <GroupedVirtuoso
            key={`${interval.it}:${listGeneration}`}
            ref={virtuosoRef}
            firstItemIndex={firstItemIndex}
            initialTopMostItemIndex={{
              index: initIndex,
              align: "center",
              behavior: "auto",
            }}
            groupCounts={groups.map((gr) => gr.count)}
            groupContent={groupContent}
            itemContent={itemContent}
            startReached={handleStartReached}
            endReached={handleEndReached}
          />
        )}
      </div>
    </div>
  );
}
